import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import GPSCheckIn from '../components/GPSCheckIn';
import { setsApi, type ScheduledSet } from '../services/api';
import { cacheSet, getCachedSet } from '../utils/scheduleCache';
import { sanitizeDriveData } from '../utils/driveSegment';
import { useDayTrackingContext } from '../context/DayTrackingContext';
import { useOdometerContext } from '../context/OdometerContext';
import { formatDateToLocal } from '../utils/timesheetRules';
import { getEffectiveAssociateId } from '../utils/associate';
import OdometerEntry from '../components/OdometerEntry';
import RetroactiveStartDay, { type RetroactiveStartData } from '../components/RetroactiveStartDay';

function generateIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `set-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Info row component
function InfoRow({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={`flex items-center justify-between py-2 ${className}`}>
      <span className="text-sm text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value || '-'}</span>
    </div>
  );
}

// Section component
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
      <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
        {title}
      </h3>
      {children}
    </div>
  );
}

// Team member card
function TeamMemberCard({
  name,
  role,
  isYou,
}: {
  name: string;
  role: string;
  isYou: boolean;
}) {
  const roleLabels: Record<string, string> = {
    leader: 'Team Leader',
    rep1: 'Rep 1',
    rep2: 'Rep 2',
  };

  const roleColors: Record<string, string> = {
    leader: 'bg-primary-100 text-primary-700 border-primary-200',
    rep1: 'bg-blue-100 text-blue-700 border-blue-200',
    rep2: 'bg-purple-100 text-purple-700 border-purple-200',
  };

  return (
    <div className={`rounded-lg border p-3 ${roleColors[role] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">{name}</div>
          <div className="text-xs opacity-80">{roleLabels[role] || role}</div>
        </div>
        {isYou && (
          <span className="rounded-full bg-white/50 px-2 py-0.5 text-xs font-medium">
            You
          </span>
        )}
      </div>
    </div>
  );
}

export default function SetDetail() {
  const { setId } = useParams<{ setId: string }>();
  const navigate = useNavigate();
  const [set, setSet] = useState<ScheduledSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Check-in failures surface inline (retryable), not via the fatal
  // "Unable to load set" screen (route-load only). #1991
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [showOdometerEntry, setShowOdometerEntry] = useState(false);
  const [pendingGpsOverrideReason, _setPendingGpsOverrideReason] = useState<string | undefined>();
  // #2957/#3032 — after the retroactive Start-Day backfill flips the day active,
  // auto-continue the check-in the rep already asked for (no second "Check In"
  // tap, no re-prompt loop). Ref holds the freshest performCheckIn closure.
  const [autoCheckInPending, setAutoCheckInPending] = useState(false);
  const performCheckInRef =
    useRef<(gpsOverrideReason?: string, odometerReading?: number, dayAlreadyStarted?: boolean) => Promise<void>>();
  // Retroactive Start Day flow — when a rep lands on a Set page
  // without having started the day, we MUST collect departure time
  // + start odo + arrival odo before letting check-in run. SetDetail
  // previously had the same silent-startDay() bug that CallDetail did,
  // which is why Rob's row 955198 had DriveOdoStart=0 / DriveOdoEnd=1.
  const [showRetroactiveStartDay, setShowRetroactiveStartDay] = useState(false);
  const [retroactiveDriveData, setRetroactiveDriveData] = useState<{
    startTime: Date;
    endTime: Date;
    durationMinutes: number;
    startOdometer?: number;
    endOdometer?: number;
  } | null>(null);
  // Stable idempotency key for the check-in createCallLog. Same pattern
  // as useStoreVisit (#1892) — a duplicate click within the same Set
  // visit returns the same CallLog instead of creating a new row.
  // Rotated after a successful check-in.
  const [checkInKey, setCheckInKey] = useState<string>(() => generateIdempotencyKey());

  // Day tracking context
  const {
    dayData,
    startDay,
    checkIntoStore,
  } = useDayTrackingContext();

  // Odometer context
  const {
    startDayOdometer,
    recordStoreArrival,
    odometerData,
  } = useOdometerContext();

  // TODO: Odometer context - re-enable when odometer tracking is implemented
  // const { odometerData, storeCheckInOdometer } = useOdometerContext();

  // Get the effective associate ID (self, or impersonated rep when active).
  const getAssociateId = useCallback((): number | null => getEffectiveAssociateId(), []);

  // Fetch set details
  useEffect(() => {
    async function fetchSet() {
      if (!setId) return;

      setIsLoading(true);
      setError(null);

      try {
        // Fetch single set directly by ID (optimized endpoint)
        const data = await setsApi.getSetById(parseInt(setId));
        setSet(data.set);
        // Write-through to the offline cache so a later signal loss can
        // still render this set (#1986 — kills diag #33 "Unable to load
        // set"). Best-effort; never block the happy path.
        void cacheSet(parseInt(setId), data.set);
      } catch (err) {
        console.error('Failed to fetch set:', err);
        const message = err instanceof Error ? err.message : 'Failed to load set details';
        // A 403/404 is authoritative; only fall back to cache otherwise.
        const authoritative = message.includes('Not authorized') || message.includes('not found');
        const cached = authoritative
          ? null
          : await getCachedSet<ScheduledSet>(parseInt(setId)).catch(() => null);
        if (cached) {
          setSet(cached);
        } else if (message.includes('Not authorized')) {
          setError('You are not assigned to this set');
        } else if (message.includes('not found')) {
          setError('Set not found');
        } else {
          setError(message);
        }
      } finally {
        setIsLoading(false);
      }
    }

    fetchSet();
  }, [setId]);

  // Handle GPS check-in completion
  const handleGPSCheckInComplete = useCallback((gpsOverrideReason?: string) => {
    if (!set) return;

    // TODO: Add odometer tracking check when isTracking is added to OdometerData
    // For now, proceed directly with check-in
    performCheckIn(gpsOverrideReason);
  }, [set]);

  // Handle odometer submission
  const handleOdometerSubmit = useCallback(async (reading: number) => {
    if (!set) return;

    // TODO: Record the odometer reading when storeCheckInOdometer is implemented
    // storeCheckInOdometer(reading);
    setShowOdometerEntry(false);

    // Now perform the check-in
    await performCheckIn(pendingGpsOverrideReason, reading);
  }, [set, pendingGpsOverrideReason]);

  // Perform the actual check-in
  const performCheckIn = useCallback(async (
    gpsOverrideReason?: string,
    odometerReading?: number,
    dayAlreadyStarted?: boolean,
  ) => {
    if (!set) return;

    setIsCheckingIn(true);
    setCheckInError(null);

    try {
      // Use legacystoreid for navigation and DayTracking (matches URL and call log filtering)
      // Use store_id (PK) only for API body where backend expects PK
      const legacyStoreId = Number(set.store.legacy_store_id) || set.store.store_id;
      const associateId = getAssociateId();

      if (!associateId) {
        setCheckInError('Unable to check in: Please log out and log back in.');
        setIsCheckingIn(false);
        return;
      }

      // Day must already be started — same pattern as CallDetail (#1893). The
      // retroactive form blocks check-in until the rep supplies departure time +
      // odos. #2957/#3032 — dayAlreadyStarted is set by the auto-continue after
      // the retroactive backfill; skip the start-day gate then, because the
      // status flip is async and a stale closure re-opened the form, trapping the
      // rep in the "didn't start day" loop (diag #73, overdue set from Schedule).
      // Only gate the FIRST, rep-initiated tap.
      if (!dayAlreadyStarted && dayData.status === 'not_started') {
        _setPendingGpsOverrideReason(gpsOverrideReason);
        setShowRetroactiveStartDay(true);
        setIsCheckingIn(false);
        return;
      }

      // Check into store via day tracking (local state) - use legacystoreid to match URL
      const closedDriveData = await checkIntoStore(legacyStoreId, set.store.store_name, set.project_id);
      // Guard against an inverted / zero-length drive segment — the server
      // rejects stdrivetime >= eddrivetime ("Start and End Drive time not
      // selected properly"). #1991 / diag #34
      // Fill the outbound-drive odometer pair (the segment carries times only)
      // from the start-day + check-in readings so the CallLog gets both drive
      // time and mileage. Both-or-neither: the server rejects a lone driveodo,
      // so a missing side yields a time-only drive.
      const startDayOdo = odometerData.readings?.find((r) => r.type === 'start_day')?.reading;
      const outStartOdo = closedDriveData?.startOdometer ?? startDayOdo;
      const outEndOdo = closedDriveData?.endOdometer ?? odometerReading;
      const bothOdo = outStartOdo != null && outEndOdo != null;
      const closedWithOdo = closedDriveData
        ? {
            ...closedDriveData,
            startOdometer: bothOdo ? outStartOdo : undefined,
            endOdometer: bothOdo ? outEndOdo : undefined,
          }
        : closedDriveData;
      const driveDataToUse = sanitizeDriveData(retroactiveDriveData || closedWithOdo);

      // Timestamps for the check-in payload.
      const todayStr = formatDateToLocal(new Date());
      const now = new Date();
      const timeStr = now.toTimeString().slice(0, 5);

      // Queue-only check-in (#1987 Phase 3 — the sync queue is the SOLE write
      // path). `checkInKey` is this visit's localId: checkInOp.applyLocal
      // writes the visit_task_sessions row keyed by it and enqueues the POST;
      // the sync engine retries on its own until the server CallLog lands, so
      // a dropped check-in (diag #33) is invisible — the rep proceeds
      // immediately on the localId. checkInOp.reconcile later stamps the
      // server calllogid back onto this same row, which the checkout/survey
      // ops resolve lazily via visitLocalId.
      const { enqueueOp } = await import('../sync/writeOps');
      await enqueueOp(
        'checkIn',
        {
          storeId: legacyStoreId,
          projectId: set.project_id,
          associateId,
          storeName: set.store.store_name,
          gps: dayData.currentLocation
            ? {
                lat: dayData.currentLocation.lat,
                lng: dayData.currentLocation.lng,
                accuracy: dayData.currentLocation.accuracy,
              }
            : undefined,
          odometer: odometerReading,
          // Drive-start odo for the odometer-only branch (no drive segment):
          // the retroactive/closed-drive start reading, else 0. The server
          // rejects a lone driveodoend ("Both Start and End miles must be
          // entered together") — matches SetDetail's prior direct payload.
          startOdometer: driveDataToUse?.startOdometer ?? 0,
          dteCall: todayStr,
          stCallTime: `${todayStr}T${timeStr}:00`,
          gpsOverrideReason,
          driveData: driveDataToUse
            ? {
                startTime: driveDataToUse.startTime.toISOString(),
                endTime: driveDataToUse.endTime.toISOString(),
                startOdometer: driveDataToUse.startOdometer,
                endOdometer: driveDataToUse.endOdometer,
              }
            : undefined,
        },
        checkInKey,
      );

      // The localId we just enqueued under; hand it to StoreDetail below.
      const visitLocalId = checkInKey;
      // Rotate the idempotency key so a subsequent check-in attempt
      // in the same component lifetime gets a fresh localId.
      setCheckInKey(generateIdempotencyKey());

      // Navigate to StoreDetail on the visit localId. StoreDetail adopts this
      // exact session via ?visitId= so the active task shares the queue's id
      // (no server callLogId exists yet — it arrives when the queue syncs).
      const params = new URLSearchParams({
        projectId: set.project_id.toString(),
        distributorId: (set.distributor_id || 0).toString(),
        setId: set.projectset_id.toString(),
        taskType: 'set',
        projectDesc: set.project_description || '',
        clientName: set.client_name || '',
        visitId: visitLocalId,
      });
      // Team verdict rides the handoff (#3629): this page holds the set's
      // roster, and the non-lead deliverable suppression must be decided at
      // task birth — not left racing StoreDetail's background task fetch.
      const team = set.team || [];
      if (team.some((m) => m.role === 'rep1' || m.role === 'rep2')) {
        params.set('setTeam', '1');
        params.set('setLead', team.some((m) => m.is_current_user && m.role === 'leader') ? '1' : '0');
      }
      navigate(`/store/${legacyStoreId}?${params.toString()}`);
    } catch (err) {
      console.error('Failed to check in:', err);
      // Inline + retryable; not the fatal "Unable to load set" screen. #1991
      setCheckInError(err instanceof Error ? err.message : 'Failed to check in');
      setIsCheckingIn(false);
    }
  }, [set, getAssociateId, dayData, startDay, checkIntoStore, navigate]);

  // #2957/#3032 — keep the ref pointing at the freshest performCheckIn closure so
  // the auto-continue effect below calls it with the buffered drive data.
  useEffect(() => {
    performCheckInRef.current = performCheckIn;
  }, [performCheckIn]);

  // #2957/#3032 — once the retroactive Start-Day backfill flips the day active,
  // auto-continue the check-in. Fire on the pending flag ALONE (handleRetroactive-
  // StartDay sets it only AFTER awaiting startDay(), so the day is already
  // started + persisted); pass dayAlreadyStarted=true so performCheckIn skips the
  // start-day gate regardless of its (async) status closure — this is what breaks
  // the "didn't start day" loop from an overdue set (diag #73).
  useEffect(() => {
    if (!autoCheckInPending) return;
    setAutoCheckInPending(false);
    void performCheckInRef.current?.(pendingGpsOverrideReason, undefined, true);
  }, [autoCheckInPending, pendingGpsOverrideReason]);

  // Open the retroactive Start Day form. Was a silent startDay() with
  // no data — same bug as CallDetail (#1893) that gave Rob a 0->1
  // DriveOdo row because no real odo was ever entered.
  const handleStartDayAndCheckIn = useCallback(() => {
    if (dayData.status === 'not_started') {
      setShowRetroactiveStartDay(true);
    }
  }, [dayData.status]);

  // Submitted from the inline RetroactiveStartDay form. Persists the
  // captured times + odometers into day-tracking + odometer state, and
  // buffers driveDataToUse that the subsequent check-in will write.
  const handleRetroactiveStartDay = useCallback(async (data: RetroactiveStartData) => {
    if (data.startOdometer !== null) {
      await startDayOdometer(data.startOdometer, data.departureTime);
    }
    await startDay(data.departureTime);
    if (data.arrivalOdometer !== null && set) {
      const legacyStoreId = Number(set.store.legacy_store_id) || set.store.store_id;
      await recordStoreArrival(legacyStoreId, set.store.store_name ?? '', data.arrivalOdometer);
    }
    // #3019 — close the drive at the ACTUAL arrival time, not "now".
    const arrival = data.arrivalTime;
    const durationMinutes = Math.round((arrival.getTime() - data.departureTime.getTime()) / 60000);
    setRetroactiveDriveData({
      startTime: data.departureTime,
      endTime: arrival,
      durationMinutes,
      startOdometer: data.startOdometer ?? undefined,
      endOdometer: data.arrivalOdometer ?? undefined,
    });
    setShowRetroactiveStartDay(false);
    // #2957/#3032 — proceed straight into the check-in the rep already asked for,
    // instead of dropping them back to tap "Check In with GPS" again (loop).
    setAutoCheckInPending(true);
  }, [set, startDay, startDayOdometer, recordStoreArrival]);

  const handleSkipOdometerAtStartDay = useCallback(async () => {
    await startDay();
    setShowRetroactiveStartDay(false);
  }, [startDay]);

  const handleCancelStartDay = useCallback(() => {
    setShowRetroactiveStartDay(false);
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !set) {
    return (
      <div className="min-h-screen bg-slate-50 pb-24">
        <PageHeader title="Set Details" onBack={() => navigate(-1)} />
        <div className="p-4">
          <div className="rounded-xl bg-red-50 p-4 text-center text-red-700">
            <p className="font-medium">Unable to load set</p>
            <p className="mt-1 text-sm">{error || 'Set not found'}</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Parse date without timezone shift by extracting date part and using noon local time
  const dateStr = set.set_date.split('T')[0];
  const setDate = new Date(dateStr + 'T12:00:00');
  const isCompleted = set.status === 'completed';
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const isPast = setDate < today && !isCompleted;

  // Determine if we should show GPS check-in
  const showGPSCheckIn = !isCompleted && !showOdometerEntry && !isCheckingIn && !showRetroactiveStartDay;
  const needsToStartDay = dayData.status === 'not_started';

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <PageHeader
        title={set.store.store_name}
        subtitle={`#${set.project_id} ${set.project_description}`}
        onBack={() => navigate(-1)}
      />

      <main className="p-4">
        {/* Status Banner */}
        <div
          className={`mb-4 rounded-xl p-4 ${
            isCompleted
              ? 'bg-green-100 text-green-800'
              : isPast
              ? 'bg-red-100 text-red-800'
              : 'bg-primary-100 text-primary-800'
          }`}
        >
          <div className="flex items-center gap-3">
            {isCompleted ? (
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : isPast ? (
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            )}
            <div>
              <div className="font-semibold">
                {isCompleted ? 'Completed' : isPast ? 'Overdue' : 'Scheduled'}
              </div>
              <div className="text-sm opacity-90">
                {setDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 rounded-xl bg-red-50 p-4 text-center text-red-700">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Retroactive Start Day — rep arrived at a Set without
            having clicked Start Day. Must collect departure time +
            start odo + arrival odo before any check-in. Mirrors the
            CallDetail / StoreDetail pattern (#1893). */}
        {showRetroactiveStartDay && (
          <div className="mb-4">
            <RetroactiveStartDay
              storeName={set.store.store_name ?? 'this store'}
              onSubmit={handleRetroactiveStartDay}
              onCancel={handleCancelStartDay}
              onSkip={handleSkipOdometerAtStartDay}
              isLoading={isCheckingIn}
            />
          </div>
        )}

        {/* Odometer Entry */}
        {showOdometerEntry && (
          <div className="mb-4">
            <OdometerEntry
              type="store_arrival"
              onSubmit={handleOdometerSubmit}
              onCancel={() => setShowOdometerEntry(false)}
              onSkip={() => {
                setShowOdometerEntry(false);
                performCheckIn(pendingGpsOverrideReason);
              }}
              isLoading={isCheckingIn}
            />
          </div>
        )}

        {/* Check-in error (inline, retryable — not the fatal load screen). #1991 */}
        {checkInError && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="font-medium text-red-700">Check-in didn't go through</p>
            <p className="mt-1 text-sm text-red-600">{checkInError}</p>
            <p className="mt-2 text-xs text-red-500">Your info is still here — tap check-in to try again.</p>
          </div>
        )}

        {/* GPS Check-In Section */}
        {showGPSCheckIn && (
          <div className="mb-4">
            {needsToStartDay ? (
              <button
                onClick={handleStartDayAndCheckIn}
                className="w-full rounded-xl bg-primary-600 py-4 text-center font-semibold text-white shadow-lg transition-all hover:bg-primary-700 active:scale-[0.98]"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Clock In
                </span>
              </button>
            ) : set.store.lat !== null && set.store.lon !== null ? (
              <GPSCheckIn
                storeLat={set.store.lat}
                storeLng={set.store.lon}
                storeName={set.store.store_name}
                maxDistanceMeters={200}
                onCheckIn={handleGPSCheckInComplete}
                isLoading={isCheckingIn}
              />
            ) : (
              // Fallback when store has no GPS coordinates
              <button
                onClick={() => handleGPSCheckInComplete()}
                disabled={isCheckingIn}
                className="w-full rounded-xl bg-primary-600 py-4 text-center font-semibold text-white shadow-lg transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50"
              >
                Check In (GPS not available)
              </button>
            )}
          </div>
        )}

        {/* Checking In Indicator */}
        {isCheckingIn && (
          <div className="mb-4 flex items-center justify-center gap-3 rounded-xl bg-green-50 p-4">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-green-600 border-t-transparent"></div>
            <span className="font-medium text-green-700">Checking in...</span>
          </div>
        )}

        {/* Store Information */}
        <Section title="Store">
          <InfoRow label="Store #" value={set.store.legacy_store_id || set.store.store_number} />
          <InfoRow label="Address" value={set.store.address} />
          <InfoRow label="City" value={`${set.store.city}, ${set.store.state} ${set.store.zip_code}`} />
          <InfoRow label="Retailer" value={set.store.retailer_name} />
        </Section>

        {/* Team */}
        {set.team.length > 0 && (
          <Section title="Team">
            <div className="space-y-2">
              {set.team.map((member) => (
                <TeamMemberCard
                  key={member.associate_id}
                  name={member.name}
                  role={member.role}
                  isYou={member.is_current_user}
                />
              ))}
            </div>
          </Section>
        )}

        {/* Set Details */}
        <Section title="Set Details">
          <InfoRow label="Set Type" value={set.set_type_name} />
          <InfoRow label="Scheduled Hours" value={set.set_hours ? `${set.set_hours}h` : null} />
          {set.set_cost && <InfoRow label="Set Cost" value={`$${set.set_cost.toFixed(2)}`} />}
          {isCompleted && (
            <>
              <div className="my-2 border-t border-slate-200" />
              <InfoRow
                label="Completed"
                value={
                  set.completed_date
                    ? new Date(set.completed_date).toLocaleDateString()
                    : 'Yes'
                }
              />
              {set.actual_hours && (
                <InfoRow label="Actual Hours" value={`${set.actual_hours}h`} />
              )}
            </>
          )}
        </Section>

        {/* Display Specifications */}
        {(set.display_specs.chart ||
          set.display_specs.set_window ||
          set.display_specs.disp_alloc ||
          set.display_specs.inl_wide ||
          set.display_specs.inl_high) && (
          <Section title="Display Specifications">
            {set.display_specs.chart && (
              <InfoRow label="Chart #" value={set.display_specs.chart} />
            )}
            {set.display_specs.set_window && (
              <InfoRow label="Window" value={set.display_specs.set_window} />
            )}
            {set.display_specs.disp_alloc && (
              <InfoRow label="Allocation" value={set.display_specs.disp_alloc} />
            )}
            {(set.display_specs.inl_wide || set.display_specs.inl_high) && (
              <InfoRow
                label="Dimensions"
                value={`${set.display_specs.inl_wide || '?'}W x ${set.display_specs.inl_high || '?'}H`}
              />
            )}
            {set.display_specs.burn && (
              <InfoRow label="Burn" value={set.display_specs.burn} />
            )}
            {set.display_specs.prev_disp && (
              <InfoRow label="Previous Display" value={set.display_specs.prev_disp} />
            )}
          </Section>
        )}

        {/* Notes */}
        {set.notes && (
          <Section title="Notes">
            <p className="text-sm text-slate-700">{set.notes}</p>
          </Section>
        )}

        {/* Get Directions Button */}
        {!isCompleted && (
          <div className="mt-6">
            <button
              onClick={() => {
                // Open maps for directions
                const address = encodeURIComponent(
                  `${set.store.address}, ${set.store.city}, ${set.store.state} ${set.store.zip_code}`
                );
                window.open(`https://maps.google.com/maps?q=${address}`, '_blank');
              }}
              className="w-full rounded-xl border border-slate-300 bg-white py-4 text-center font-semibold text-slate-700 transition-all hover:bg-slate-50 active:scale-[0.98]"
            >
              Get Directions
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
